/**
 * Number formatting for the UI. Pure, so it is unit-testable — these read out
 * as facts about a model and a wrong unit is a wrong fact.
 */

/**
 * Bytes in binary units (KiB/MiB/GiB), because that is what a byte offset in a
 * file actually is. Model card prose usually quotes decimal GB; the difference
 * is ~7% at GiB scale, which is enough to make a reader think two numbers
 * disagree — so the unit is always spelled out rather than abbreviated to "GB".
 */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes)) return "—";
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KiB", "MiB", "GiB", "TiB"];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value < 10 ? value.toFixed(2) : value < 100 ? value.toFixed(1) : Math.round(value)} ${units[unit]}`;
}

/**
 * Parameter counts in the units the field actually speaks: K/M/B, decimal.
 * (Parameters are counted, not addressed, so binary prefixes would be wrong here
 * even though they are right for `formatBytes`.)
 */
export function formatParams(count: number): string {
  if (!Number.isFinite(count)) return "—";
  if (count < 1000) return String(count);
  const units: Array<[number, string]> = [
    /*
     * `T` exists because the frontier reached it and the ladder had not.
     *
     * `moonshotai/Kimi-K3` rendered as **2779B params** — arithmetically right,
     * and unreadable next to a model card that says 2.8T. A reader comparing
     * the two sees a number that does not obviously match and has no reason to
     * assume ours is the correct one. The tool's whole claim is that its
     * figures can be checked against the source, so a unit that makes checking
     * harder costs more than the line it saves.
     */
    [1e12, "T"],
    [1e9, "B"],
    [1e6, "M"],
    [1e3, "K"],
  ];
  for (const [scale, suffix] of units) {
    if (count >= scale) {
      const value = count / scale;
      return `${value < 10 ? value.toFixed(2) : value < 100 ? value.toFixed(1) : Math.round(value)}${suffix}`;
    }
  }
  return String(count);
}

/** `[4096, 11008]` → `4096 × 11008`; a scalar reads as "scalar", not "". */
export function formatShape(shape: readonly number[]): string {
  return shape.length === 0 ? "scalar" : shape.join(" × ");
}

/** Short commit SHA, as everyone writes them. */
export function shortSha(sha: string): string {
  return sha.slice(0, 7);
}
